const DAY_MS = 86400000;
const HOUR_MS = 3600000;
const MINUTE_MS = 60000;

// Ordered coarsest -> finest. msPerTick is the average ms between ticks for
// this (intervalType, interval) combination; used only for label-count
// estimation, not for tick placement (CanvasJS handles that from the
// intervalType/interval pair and always lands on natural boundaries).
const BANDS = [
	{ intervalType: "year", interval: 1, msPerTick: 365.25 * DAY_MS },
	{ intervalType: "month", interval: 6, msPerTick: 30.4375 * 6 * DAY_MS },
	{ intervalType: "month", interval: 3, msPerTick: 30.4375 * 3 * DAY_MS },
	{ intervalType: "month", interval: 1, msPerTick: 30.4375 * DAY_MS },
	{ intervalType: "week", interval: 1, msPerTick: 7 * DAY_MS },
	{ intervalType: "day", interval: 2, msPerTick: 2 * DAY_MS },
	{ intervalType: "day", interval: 1, msPerTick: DAY_MS },
	{ intervalType: "hour", interval: 12, msPerTick: 12 * HOUR_MS },
	{ intervalType: "hour", interval: 6, msPerTick: 6 * HOUR_MS },
	{ intervalType: "hour", interval: 3, msPerTick: 3 * HOUR_MS },
	{ intervalType: "hour", interval: 1, msPerTick: HOUR_MS },
	{ intervalType: "minute", interval: 30, msPerTick: 30 * MINUTE_MS },
	{ intervalType: "minute", interval: 15, msPerTick: 15 * MINUTE_MS },
	{ intervalType: "minute", interval: 5, msPerTick: 5 * MINUTE_MS },
	{ intervalType: "minute", interval: 2, msPerTick: 2 * MINUTE_MS },
	{ intervalType: "minute", interval: 1, msPerTick: MINUTE_MS },
	{ intervalType: "second", interval: 30, msPerTick: 30000 },
	{ intervalType: "second", interval: 15, msPerTick: 15000 },
	{ intervalType: "second", interval: 5, msPerTick: 5000 },
	{ intervalType: "second", interval: 1, msPerTick: 1000 },
];

// Ideal band for a given span, assuming plenty of horizontal space. Returns
// an index into BANDS. These thresholds preserve the previous "1st of every
// month for a year, hourly for a day, ..." reading experience on wide charts.
function idealBandIndex(spanMs) {
	const days = spanMs / DAY_MS;
	const hours = spanMs / HOUR_MS;
	const minutes = spanMs / MINUTE_MS;

	if (days >= 3650) return 0; // year, 1
	if (days >= 1825) return 1; // month, 6
	if (days >= 730) return 2; // month, 3
	if (days >= 180) return 3; // month, 1
	if (days >= 60) return 4; // week, 1
	if (days >= 20) return 5; // day, 2
	if (days >= 5) return 6; // day, 1
	if (hours >= 48) return 8; // hour, 6
	if (hours >= 12) return 10; // hour, 1
	if (hours >= 6) return 11; // minute, 30
	if (hours >= 2) return 12; // minute, 15
	if (minutes >= 45) return 13; // minute, 5
	if (minutes >= 15) return 14; // minute, 2
	if (minutes >= 5) return 15; // minute, 1
	return 16; // second, 30
}

// Conservative horizontal footprint for a -30 degree tilted label at 12px
// font. Covers both short ("HH:mm") and longer ("DD MMM YY") formats.
const MIN_LABEL_PX = 50;
const MIN_LABELS = 3;

// Pin x-axis ticks to natural calendar boundaries (1st of month, Monday,
// midnight) so labels stay put across resize/maximise instead of drifting
// with CanvasJS's pixel-width auto interval. On wide charts the label
// spacing matches the pre-existing behaviour; on narrow charts the ideal
// band is promoted to the next coarser one until the label count fits the
// available pixel budget, so mobile views don't overlap.
export function axisTickInterval(min, max, chartWidthPx) {
	if (!min || !max) {
		return null;
	}

	const spanMs = max - min;
	if (spanMs <= 0) {
		return null;
	}

	let bandIndex = idealBandIndex(spanMs);

	if (chartWidthPx) {
		const widthCap = Math.max(MIN_LABELS, Math.floor(chartWidthPx / MIN_LABEL_PX));
		while (bandIndex > 0 && spanMs / BANDS[bandIndex].msPerTick > widthCap) {
			bandIndex--;
		}
	}

	const band = BANDS[bandIndex];
	return { intervalType: band.intervalType, interval: band.interval };
}
